// ============================================================================
// Gmail — `users.messages.get` + `users.messages.modify` helpers.
//
// The capability exposes two shapes:
//   - `getEmail(messageId)` → full `EmailFull` (headers + body + attachments).
//   - `markAsRead(messageId)` → removes the `UNREAD` label.
//
// Strategy for `getEmail`:
//   We request `format=raw` which returns the base64url-encoded RFC 2822
//   source. That's the authoritative representation — decoding it via our
//   hand-rolled `parseMimeMessage` (mime.ts) gives us the full header map,
//   body parts, and inline / regular attachments WITHOUT having to walk the
//   `payload` tree Google returns for `format=full`. The tradeoff: we do our
//   own charset / encoding handling, which we'd need for Thai QP anyway.
// ============================================================================

import type { EmailFull } from '../../capabilities/email'
import { GMAIL_API_BASE } from './oauth'
import {
  base64UrlDecodeToString,
  parseMimeMessage,
} from './mime'
import {
  classifyGmailError,
  GmailError,
  parseRetryAfterMs,
  sanitizeGmailErrorMessage,
  type GmailMessage,
  type GoogleErrorEnvelope,
} from './types'

// ============================================================================
// Types
// ============================================================================

export interface GetGmailMessageInput {
  accessToken: string
  messageId: string
  /** Default `raw` — the parser in `mime.ts` handles the rest. */
  format?: 'full' | 'raw'
  fetchImpl?: typeof fetch
  apiBase?: string
}

export interface MarkGmailAsReadInput {
  accessToken: string
  messageId: string
  fetchImpl?: typeof fetch
  apiBase?: string
}

// ============================================================================
// getGmailMessage — fetch + decode + parse
// ============================================================================

export async function getGmailMessage(args: GetGmailMessageInput): Promise<EmailFull> {
  assertAccessToken(args.accessToken)
  assertMessageId(args.messageId)
  const fetchImpl = args.fetchImpl ?? fetch
  const apiBase = args.apiBase ?? GMAIL_API_BASE
  const format = args.format ?? 'raw'

  const url = new URL(`${apiBase}/gmail/v1/users/me/messages/${encodeURIComponent(args.messageId)}`)
  url.searchParams.set('format', format)

  const resp = await fetchImpl(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${args.accessToken}`,
    },
  })
  if (!resp.ok) {
    throw await errorFromResponse(resp)
  }

  const body = (await resp.json()) as GmailMessage
  if (!body || typeof body.id !== 'string') {
    throw new GmailError(
      'PROVIDER_ERROR',
      'Gmail returned an unexpected messages.get shape',
    )
  }

  if (format === 'raw') {
    if (typeof body.raw !== 'string' || body.raw.length === 0) {
      throw new GmailError(
        'PROVIDER_ERROR',
        'Gmail returned format=raw without a `raw` field',
      )
    }
    const rfc2822 = base64UrlDecodeToString(body.raw)
    return parseMimeMessage(rfc2822, {
      messageId: body.id,
      threadId: body.threadId,
      labelIds: body.labelIds,
      isUnread: body.labelIds?.includes('UNREAD') ?? false,
      snippet: body.snippet,
      internalDate: body.internalDate,
    })
  }

  // `format=full` path: we reconstruct RFC 2822 from the payload tree, then
  // feed it into the same parser. Simpler than walking the tree twice and
  // keeps the downstream contract identical.
  const rfc2822 = serializeFullPayload(body)
  return parseMimeMessage(rfc2822, {
    messageId: body.id,
    threadId: body.threadId,
    labelIds: body.labelIds,
    isUnread: body.labelIds?.includes('UNREAD') ?? false,
    snippet: body.snippet,
    internalDate: body.internalDate,
  })
}

/**
 * Reconstruct an RFC 2822 MIME source from Gmail's `format=full` payload tree.
 *
 * @deprecated Issue #254 M2: the `format=full` reconstruction path is NOT
 *   attachment-safe. `serializePart` below decodes attachment part bodies with
 *   `base64UrlDecodeToString`, which UTF-8-decodes raw bytes — binary
 *   attachments get corrupted before they reach the downstream parser. The
 *   `format=raw` path already works correctly for every use case (text, Thai,
 *   binary). This function throws a `PROVIDER_ERROR` when the `format=full`
 *   response carries attachments so a caller that accidentally opted into
 *   `format=full` surfaces the issue immediately instead of silently receiving
 *   a mangled file.
 */
function serializeFullPayload(msg: GmailMessage): string {
  if (!msg.payload) return ''
  if (payloadHasAttachments(msg.payload)) {
    throw new GmailError(
      'PROVIDER_ERROR',
      'format=full attachment path is deprecated (issue #254 M2) — use format=raw',
    )
  }
  const headers = Array.isArray(msg.payload.headers) ? msg.payload.headers : []
  const headerLines = headers.map((h) => `${h.name}: ${h.value}`)
  const bodyStr = serializePart(msg.payload)
  return `${headerLines.join('\r\n')}\r\n\r\n${bodyStr}`
}

/** Recursively walks the Gmail payload tree looking for an attachment part. */
function payloadHasAttachments(part: {
  filename?: string
  body?: { attachmentId?: string; data?: string }
  parts?: Array<{
    filename?: string
    body?: { attachmentId?: string; data?: string }
    parts?: unknown[]
  }>
}): boolean {
  // Gmail marks attachment parts with `filename` + `body.attachmentId`. A
  // `body.attachmentId` alone is the strongest signal (the bytes live on a
  // separate endpoint).
  if (part.body?.attachmentId) return true
  if (Array.isArray(part.parts)) {
    for (const child of part.parts) {
      if (
        payloadHasAttachments(
          child as Parameters<typeof payloadHasAttachments>[0],
        )
      ) {
        return true
      }
    }
  }
  return false
}

function serializePart(part: {
  mimeType?: string
  headers?: Array<{ name: string; value: string }>
  body?: { data?: string }
  parts?: Array<{
    mimeType?: string
    headers?: Array<{ name: string; value: string }>
    body?: { data?: string }
    parts?: unknown[]
  }>
}): string {
  if (Array.isArray(part.parts) && part.parts.length > 0) {
    const ct = (part.headers ?? []).find((h) => h.name.toLowerCase() === 'content-type')?.value ?? ''
    const boundary = /boundary="?([^";\s]+)"?/.exec(ct)?.[1] ?? '----=_BOUNDARY_FB'
    const parts = part.parts.map((p) => {
      const h = Array.isArray((p as { headers?: Array<{ name: string; value: string }> }).headers)
        ? (p as { headers: Array<{ name: string; value: string }> }).headers
        : []
      const headerStr = h.map((x) => `${x.name}: ${x.value}`).join('\r\n')
      // Recursive — cast for TS.
      const child = serializePart(p as Parameters<typeof serializePart>[0])
      return `${headerStr}\r\n\r\n${child}`
    })
    return `--${boundary}\r\n${parts.join(`\r\n--${boundary}\r\n`)}\r\n--${boundary}--\r\n`
  }
  return part.body?.data ? base64UrlDecodeToString(part.body.data) : ''
}

// ============================================================================
// markGmailAsRead — remove the UNREAD label
// ============================================================================

export async function markGmailAsRead(args: MarkGmailAsReadInput): Promise<void> {
  assertAccessToken(args.accessToken)
  assertMessageId(args.messageId)
  const fetchImpl = args.fetchImpl ?? fetch
  const apiBase = args.apiBase ?? GMAIL_API_BASE

  const url = `${apiBase}/gmail/v1/users/me/messages/${encodeURIComponent(args.messageId)}/modify`
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${args.accessToken}`,
    },
    body: JSON.stringify({ removeLabelIds: ['UNREAD'] }),
  })
  if (!resp.ok) {
    throw await errorFromResponse(resp)
  }
}

// ============================================================================
// Helpers
// ============================================================================

async function errorFromResponse(response: Response): Promise<GmailError> {
  let envelope: GoogleErrorEnvelope | null = null
  let text = ''
  try {
    text = await response.text()
    envelope = text ? (JSON.parse(text) as GoogleErrorEnvelope) : null
  } catch {
    envelope = null
  }
  const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'))
  if (!envelope && text) {
    return new GmailError(
      response.status === 401 ? 'AUTH_FAILED' : 'PROVIDER_ERROR',
      sanitizeGmailErrorMessage(text).slice(0, 200),
      { httpStatus: response.status, retryAfterMs },
    )
  }
  return classifyGmailError(
    response.status,
    envelope?.error?.errors?.[0]?.reason,
    envelope,
    retryAfterMs,
  )
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new GmailError('INVALID_INPUT', 'accessToken is required')
  }
}

function assertMessageId(id: string | undefined | null): void {
  if (!id || typeof id !== 'string' || id.length === 0) {
    throw new GmailError('INVALID_INPUT', 'messageId is required')
  }
}
